import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';

const Clients = ({ user }) => {
    const isAdmin = user?.role === 'admin';
    // Estado para armazenar a lista de clientes (carregada da API)
    const [clients, setClients] = useState([]);
    const [loadingClients, setLoadingClients] = useState(true);

    // Estado para armazenar os dados do novo cliente
    const [newClient, setNewClient] = useState({ 
        id: '', 
        name: '', 
        anthropicToken: '',
        openaiToken: '',
        deepseekToken: ''
    });

    // Estado para mensagens de feedback
    const [message, setMessage] = useState({ type: '', text: '' });

    // Carregar clientes da API ao montar o componente
    useEffect(() => {
        const fetchClients = async () => {
            try {
                const response = await axios.get(`${API_BASE}/api/clients`);
                setClients(Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                console.error('Erro ao carregar clientes:', error);
                setMessage({ type: 'error', text: 'Erro ao carregar a lista de clientes.' });
                setClients([]);
            } finally {
                setLoadingClients(false);
            }
        };
        fetchClients();
    }, []);

    // Função para adicionar um novo cliente
    const handleAddClient = async () => {
        if (!newClient.id || !newClient.name || 
            (!newClient.anthropicToken && !newClient.openaiToken && !newClient.deepseekToken)) {
            setMessage({ 
                type: 'error', 
                text: 'Por favor, preencha o ID, nome e pelo menos um token de API.' 
            });
            return;
        }

        const clientExists = clients.some(client => client.id === newClient.id);
        if (clientExists) {
            setMessage({ type: 'error', text: 'Já existe um cliente com este ID.' });
            return;
        }

        try {
            // Envia o cliente para o backend
            const response = await axios.post(`${API_BASE}/api/clients`, {
                id: newClient.id,
                name: newClient.name,
                anthropicToken: newClient.anthropicToken,
                openaiToken: newClient.openaiToken,
                deepseekToken: newClient.deepseekToken
            });

            // Recarrega a lista da API para refletir o novo cliente
            const listRes = await axios.get(`${API_BASE}/api/clients`);
            setClients(Array.isArray(listRes.data) ? listRes.data : []);
            setNewClient({ 
                id: '', 
                name: '', 
                anthropicToken: '',
                openaiToken: '',
                deepseekToken: '' 
            });
            setMessage({ type: 'success', text: response.data.message || 'Cliente cadastrado com sucesso!' });
        } catch (error) {
            console.error('Erro ao cadastrar cliente:', error);
            setMessage({ 
                type: 'error', 
                text: error.response?.data?.error || 'Erro ao cadastrar cliente. Verifique sua conexão.' 
            });
        }
    };

    // Função para remover um cliente (exclui no backend/MongoDB e atualiza a lista)
    const handleRemoveClient = async (id) => {
        const confirmRemove = window.confirm('Tem certeza que deseja descadastrar este cliente?');
        if (!confirmRemove) return;
        try {
            await axios.delete(`${API_BASE}/api/clients/${encodeURIComponent(id)}`);
            setClients(prev => prev.filter(client => client.id !== id));
            setMessage({ type: 'success', text: 'Cliente descadastrado com sucesso!' });
        } catch (error) {
            console.error('Erro ao descadastrar cliente:', error);
            setMessage({
                type: 'error',
                text: error.response?.data?.error || 'Erro ao descadastrar cliente. Tente novamente.'
            });
        }
    };

    return (
        <div style={styles.container}>
            <h2 style={styles.title}>Gerenciamento de Clientes</h2>

            {/* Mensagem de feedback */}
            {message.text && (
                <div
                    style={{
                        ...styles.message,
                        backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
                        color: message.type === 'success' ? '#155724' : '#721c24',
                    }}
                >
                    {message.text}
                </div>
            )}

            {/* Formulário para cadastrar novo cliente — apenas admin */}
            {isAdmin && (
            <div style={styles.form}>
                <h3 style={styles.subtitle}>Cadastrar Novo Cliente</h3>
                <div style={styles.inputGroup}>
                    <label style={styles.label}>ID do Cliente:</label>
                    <input
                        type="text"
                        placeholder="ID do Cliente"
                        value={newClient.id}
                        onChange={(e) => setNewClient({ ...newClient, id: e.target.value })}
                        style={styles.input}
                    />
                </div>
                
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Nome do Cliente:</label>
                    <input
                        type="text"
                        placeholder="Nome do Cliente"
                        value={newClient.name}
                        onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                        style={styles.input}
                    />
                </div>
                
                <div style={styles.inputGroup}>
                    <label style={styles.label}>Token da Anthropic:</label>
                    <input
                        type="text"
                        placeholder="Token da API Anthropic (Claude)"
                        value={newClient.anthropicToken}
                        onChange={(e) => setNewClient({ ...newClient, anthropicToken: e.target.value })}
                        style={styles.input}
                    />
                </div>

                <div style={styles.inputGroup}>
                    <label style={styles.label}>Token da OpenAI:</label>
                    <input
                        type="text"
                        placeholder="Token da API OpenAI (GPT)"
                        value={newClient.openaiToken}
                        onChange={(e) => setNewClient({ ...newClient, openaiToken: e.target.value })}
                        style={styles.input}
                    />
                </div>

                <div style={styles.inputGroup}>
                    <label style={styles.label}>Token da DeepSeek:</label>
                    <input
                        type="text"
                        placeholder="Token da API DeepSeek"
                        value={newClient.deepseekToken}
                        onChange={(e) => setNewClient({ ...newClient, deepseekToken: e.target.value })}
                        style={styles.input}
                    />
                </div>
                
                <button onClick={handleAddClient} style={styles.button}>Cadastrar</button>
            </div>
            )}

            {/* Lista de clientes cadastrados */}
            <div style={styles.list}>
                <h3 style={styles.subtitle}>Clientes Cadastrados</h3>
                {loadingClients ? (
                    <p style={styles.loadingText}>Carregando clientes...</p>
                ) : clients.length > 0 ? (
                    <ul style={styles.ul}>
                        {clients.map(client => (
                            <li key={client.id} style={styles.li}>
                                <div style={styles.clientInfo}>
                                    <h4 style={styles.clientName}>{client.name}</h4>
                                    <p style={styles.clientId}>ID: {client.id}</p>
                                    <div style={styles.tokenInfo}>
                                        <span style={styles.tokenLabel}>
                                            Anthropic: 
                                            <span style={{
                                                color: (client.hasAnthropicToken || client.anthropicToken) ? '#00D4FF' : '#6B7280',
                                                marginLeft: '5px'
                                            }}>
                                                {(client.hasAnthropicToken || client.anthropicToken) ? '✓' : '✗'}
                                            </span>
                                        </span>
                                        <span style={styles.tokenLabel}>
                                            OpenAI: 
                                            <span style={{
                                                color: (client.hasOpenAIToken || client.openaiToken) ? '#00D4FF' : '#6B7280',
                                                marginLeft: '5px'
                                            }}>
                                                {(client.hasOpenAIToken || client.openaiToken) ? '✓' : '✗'}
                                            </span>
                                        </span>
                                        <span style={styles.tokenLabel}>
                                            DeepSeek: 
                                            <span style={{
                                                color: (client.hasDeepSeekToken || client.deepseekToken) ? '#00D4FF' : '#6B7280',
                                                marginLeft: '5px'
                                            }}>
                                                {(client.hasDeepSeekToken || client.deepseekToken) ? '✓' : '✗'}
                                            </span>
                                        </span>
                                    </div>
                                </div>

                                <div style={styles.buttonGroup}>
                                    {isAdmin && (
                                    <button
                                        onClick={() => handleRemoveClient(client.id)}
                                        style={styles.removeButton}
                                    >
                                        Descadastrar
                                    </button>
                                    )}

                                    {/* Botão para editar a customização */}
                                    <button
                                        onClick={() => alert("Para editar a customização, use a aba Customização no menu principal.")}
                                        style={styles.editButton}
                                    >
                                        Editar Customização
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p>Nenhum cliente cadastrado.</p>
                )}
            </div>
        </div>
    );
};

// Estilos futuristas para o layout
const styles = {
    container: {
        padding: '30px',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
        maxWidth: '900px',
        margin: '0 auto',
    },
    title: {
        textAlign: 'center',
        fontSize: '2rem',
        fontWeight: '700',
        background: 'linear-gradient(135deg, #B026FF 0%, #00D4FF 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
        textTransform: 'uppercase',
        letterSpacing: '2px',
        marginBottom: '30px',
    },
    subtitle: {
        marginBottom: '20px',
        color: '#00D4FF',
        fontSize: '1.2rem',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '1px',
    },
    form: {
        marginBottom: '40px',
        padding: '30px',
        background: 'linear-gradient(135deg, rgba(10, 14, 39, 0.9) 0%, rgba(176, 38, 255, 0.4) 100%)',
        borderRadius: '15px',
        border: '1px solid rgba(0, 212, 255, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 0 30px rgba(0, 212, 255, 0.1)',
        backdropFilter: 'blur(10px)',
    },
    inputGroup: {
        marginBottom: '20px',
    },
    label: {
        display: 'block',
        marginBottom: '10px',
        fontWeight: '600',
        color: '#B0BEC5',
        fontSize: '14px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
    },
    input: {
        display: 'block',
        width: '100%',
        padding: '12px 15px',
        marginBottom: '10px',
        border: '1px solid rgba(0, 212, 255, 0.3)',
        borderRadius: '8px',
        fontSize: '14px',
        background: 'rgba(10, 14, 39, 0.8)',
        color: '#FFFFFF',
        backdropFilter: 'blur(10px)',
        transition: 'all 0.3s ease',
    },
    button: {
        padding: '15px 30px',
        background: 'linear-gradient(135deg, #B026FF 0%, #0066FF 100%)',
        color: 'white',
        border: '1px solid #00D4FF',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '16px',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '1px',
        boxShadow: '0 0 20px rgba(255, 107, 53, 0.3)',
        transition: 'all 0.3s ease',
        width: '100%',
    },
    list: {
        padding: '30px',
        background: 'linear-gradient(135deg, rgba(10, 14, 39, 0.9) 0%, rgba(176, 38, 255, 0.4) 100%)',
        borderRadius: '15px',
        border: '1px solid rgba(0, 212, 255, 0.3)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 0 30px rgba(0, 212, 255, 0.1)',
        backdropFilter: 'blur(10px)',
    },
    ul: {
        listStyleType: 'none',
        padding: '0',
    },
    li: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '20px',
        marginBottom: '15px',
        background: 'rgba(176, 38, 255, 0.3)',
        borderRadius: '10px',
        border: '1px solid rgba(0, 212, 255, 0.2)',
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
        transition: 'all 0.3s ease',
    },
    clientInfo: {
        flex: '1',
    },
    clientName: {
        margin: '0 0 8px 0',
        fontSize: '18px',
        fontWeight: '700',
        color: '#FFFFFF',
    },
    clientId: {
        margin: '0 0 15px 0',
        color: '#B0BEC5',
        fontSize: '14px',
        fontFamily: 'monospace',
    },
    tokenInfo: {
        display: 'flex',
        gap: '20px',
        margin: '10px 0',
        flexWrap: 'wrap',
    },
    tokenLabel: {
        fontSize: '13px',
        color: '#B0BEC5',
        fontWeight: '500',
    },
    buttonGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    removeButton: {
        padding: '10px 20px',
        background: 'linear-gradient(135deg, #f44336 0%, #c62828 100%)',
        color: 'white',
        border: '1px solid rgba(255, 107, 53, 0.5)',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        transition: 'all 0.3s ease',
    },
    editButton: {
        padding: '10px 20px',
        background: 'linear-gradient(135deg, #00D4FF 0%, #0066FF 100%)',
        color: 'white',
        border: '1px solid rgba(0, 212, 255, 0.5)',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        transition: 'all 0.3s ease',
    },
    message: {
        padding: '15px 20px',
        marginBottom: '25px',
        borderRadius: '8px',
        textAlign: 'center',
        fontWeight: '600',
        fontSize: '14px',
        textTransform: 'uppercase',
        letterSpacing: '1px',
    },
    loadingText: {
        color: '#B0BEC5',
        fontSize: '14px',
    },
};

export default Clients;
